import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/common/Header';
import ConfirmModal from '../../components/common/ConfirmModal';
import { changePasswordApi, changeNameApi, verifyPasswordApi, getServiceLinksApi } from '../../api';
import { PATHS } from '../../routes/paths';

const MyPage = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Password Change State
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmChangeOpen, setConfirmChangeOpen] = useState(false);

  // Name Change State (Admin Only)
  const [nameForm, setNameForm] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameSuccessOpen, setNameSuccessOpen] = useState(false);

  useEffect(() => {
    const token = window.localStorage.getItem('accessToken');
    const rawUser = window.localStorage.getItem('currentUser');
    if (!token || !rawUser) {
      navigate(PATHS.login, { replace: true });
      return;
    }
    
    try {
      const parsed = JSON.parse(rawUser);
      setCurrentUser(parsed);
      setIsAdmin(parsed?.role === 'ADMIN');
      setNameForm(parsed?.name || '');
    } catch {
      navigate(PATHS.login, { replace: true });
    }
  }, [navigate]);

  const handleOpenPasswordFind = async () => {
    try {
      const res = await getServiceLinksApi();
      const items = res?.data || [];
      const url = items.find((it) => it?.key === 'passwordFind')?.url;
      const u = String(url || '').trim();
      if (!u) return;
      window.open(u, '_blank', 'noopener,noreferrer');
    } catch {
      // 링크 조회 실패 시 조용히 무시
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'newPassword' || field === 'confirmPassword') {
        setErrors((prevErrors) => ({
          ...prevErrors,
          confirmPassword:
            next.confirmPassword && next.confirmPassword !== next.newPassword
              ? '비밀번호를 확인해주세요.'
              : '',
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }));
      }

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    const trimmedName = nameForm.trim();
    const nameChanged = isAdmin && trimmedName && trimmedName !== (currentUser?.name || '');
    const wantsNameChange = Boolean(nameChanged);

    const newErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    if (isAdmin) {
      if (trimmedName && trimmedName.length > 10) {
        setNameError('활동명은 10자 이내여야 합니다.');
      } else {
        setNameError('');
      }
    }

    const hasAnyPasswordInput =
      form.currentPassword.trim() || form.newPassword.trim() || form.confirmPassword.trim();
    const wantsPwdChange = Boolean(hasAnyPasswordInput);

    if (wantsPwdChange) {
      if (!form.currentPassword.trim()) {
        newErrors.currentPassword = '기존 비밀번호를 입력해주세요.';
      }
      if (!form.newPassword.trim()) {
        newErrors.newPassword = '새 비밀번호를 입력해주세요.';
      }
      if (!form.confirmPassword.trim()) {
        newErrors.confirmPassword = '비밀번호 확인을 입력해주세요.';
      } else if (form.confirmPassword !== form.newPassword) {
        newErrors.confirmPassword = '입력하신 비밀번호와 다릅니다.';
      }
    }

    setErrors(newErrors);
    const hasError = Object.values(newErrors).some((msg) => msg);
    const hasNameError = Boolean(nameError);

    if (!wantsNameChange && !wantsPwdChange) {
      setErrorMessage('변경할 내용을 입력해주세요.');
      return;
    }

    if (hasError || hasNameError) return;

    // 비밀번호 검증을 먼저 수행
    if (wantsPwdChange) {
      try {
        setSubmitting(true);
        await verifyPasswordApi({ currentPassword: form.currentPassword });
      } catch (err) {
        setSubmitting(false);
        setErrors((prev) => ({
          ...prev,
          currentPassword: err.message || '현재 비밀번호가 올바르지 않습니다.',
        }));
        return;
      } finally {
        setSubmitting(false);
      }
    }

    setErrorMessage('');
    setConfirmChangeOpen(true);
  };

  const handleConfirmChange = async () => {
    try {
      setSubmitting(true);
      const trimmedName = nameForm.trim();
      const nameChanged = isAdmin && trimmedName && trimmedName !== (currentUser?.name || '');
      const wantsNameChange = Boolean(nameChanged);
      const hasAnyPasswordInput =
        form.currentPassword.trim() || form.newPassword.trim() || form.confirmPassword.trim();
      const wantsPwdChange = Boolean(hasAnyPasswordInput);

      if (!wantsNameChange && !wantsPwdChange) {
        setErrorMessage('변경할 내용을 입력해주세요.');
        return;
      }

      if (wantsPwdChange) {
        await changePasswordApi({
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        });
        setErrorMessage('');
        setSuccessOpen(true);
      }

      if (wantsNameChange) {
        // 비밀번호 변경이 필요한 경우 이미 위에서 성공한 상태
        const res = await changeNameApi({ newName: trimmedName });
        const updatedUser = { ...currentUser, name: res.data.name };
        window.localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        setCurrentUser(updatedUser);
        setNameSuccessOpen(true);
      }

    } catch (err) {
      const message = err.message || '비밀번호 변경 중 오류가 발생했습니다.';
      if (err?.status === 401 || err?.suppressAlert) return;
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
      setConfirmChangeOpen(false);
    }
  };

  const handleSuccessConfirm = () => {
    setSuccessOpen(false);
    setForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    // 비밀번호 변경 후 로그아웃 시키거나 홈으로 이동
    navigate(PATHS.root);
  };

  return (
    <>
      <Box>
        <Box
          sx={{
            maxWidth: 430,
            mx: 'auto',
            bgcolor: 'white',
            minHeight: '100vh',
            borderRadius: 0,
            boxShadow: '0 0 20px rgba(0,0,0,0.06)',
            px: 2,
            position: 'relative',
          }}
        >
          <Header variant="simple" />

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, textAlign: 'center', mb: 3 }}
          >
            마이페이지
          </Typography>

          {isAdmin && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                이름 변경
              </Typography>
              <TextField
                label="이름"
                fullWidth
                value={nameForm}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 10);
                  setNameForm(value);
                  setNameError('');
                }}
                error={Boolean(nameError)}
                helperText={nameError || ' '}
                inputProps={{ maxLength: 10 }}
              />
              <Divider sx={{ my: 3 }} />
            </Box>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            비밀번호 변경
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <TextField
              type="password"
              label="기존 비밀번호"
              fullWidth
              value={form.currentPassword}
              onChange={handleChange('currentPassword')}
              error={Boolean(errors.currentPassword)}
              helperText={errors.currentPassword || ' '}
            />
            <TextField
              type="password"
              label="새 비밀번호"
              fullWidth
              value={form.newPassword}
              onChange={handleChange('newPassword')}
              error={Boolean(errors.newPassword)}
              helperText={errors.newPassword || ' '}
            />
            <TextField
              type="password"
              label="비밀번호 확인"
              fullWidth
              value={form.confirmPassword}
              onChange={(e) => {
                const value = e.target.value;
                setForm((prev) => ({ ...prev, confirmPassword: value }));
                setErrors((prev) => ({
                  ...prev,
                  confirmPassword:
                    !value
                      ? prev.confirmPassword
                      : value === form.newPassword
                      ? ''
                      : '입력하신 비밀번호와 다릅니다.',
                }));
              }}
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword || ' '}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 1,
                py: 1.2,
                bgcolor: '#0d47a1',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#0b3a81',
                },
              }}
              disabled={submitting}
            >
              {isAdmin ? '정보 수정' : '비밀번호 변경'}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 1,
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              color: '#0d47a1',
            }}
            onClick={handleOpenPasswordFind}
          >
            ※ 비밀번호 찾기는 관리자에게 문의하세요.
          </Typography>
          {errorMessage && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mt: 1 }}
            >
              {errorMessage}
            </Typography>
          )}
        </Box>
      </Box>

      <ConfirmModal
        open={confirmChangeOpen}
        message={isAdmin ? '관리자 정보를 수정하시겠습니까?' : '비밀번호를 변경하시겠습니까?'}
        confirmText="변경"
        cancelText="취소"
        onConfirm={handleConfirmChange}
        onCancel={() => setConfirmChangeOpen(false)}
      />
      <ConfirmModal
        open={successOpen}
        message={isAdmin ? '관리자 정보가 변경되었습니다.' : '비밀번호가 변경되었습니다.'}
        onConfirm={handleSuccessConfirm}
      />
      <ConfirmModal
        open={nameSuccessOpen}
        message="관리자 정보가 변경되었습니다."
        onConfirm={() => setNameSuccessOpen(false)}
      />
    </>
  );
};

export default MyPage;


